import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useRef, useState } from 'react'
import { useUploadContext } from '~/features/importer'
import { SetupWizard } from '~/templates/wizard/presets/setup'

import { Heading1, Icon, Notice, buttonVariants, cn } from '@hap/components'

const AUDIO_ACCEPTED_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.ogg']

export const Route = createFileRoute('/_authenticated/$alias/setup/upload')({
  component: RouteComponent,
})

function RouteComponent() {
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)
  const { startUploads } = useUploadContext()

  const handleFiles = useCallback(
    (files: File[]) => {
      const audioFiles = files.filter((file) => {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase()
        return AUDIO_ACCEPTED_EXTENSIONS.includes(ext)
      })
      if (audioFiles.length === 0) return

      startUploads(audioFiles)
      navigate({ to: '/$alias/setup/customize', from: Route.fullPath })
    },
    [startUploads, navigate],
  )

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    setIsDragging(true)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragging(false)
    }
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      dragCounter.current = 0
      setIsDragging(false)

      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) handleFiles(files)
    },
    [handleFiles],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) return
      const files = Array.from(e.target.files)
      if (files.length > 0) handleFiles(files)
      if (inputRef.current) inputRef.current.value = ''
    },
    [handleFiles],
  )

  return (
    <SetupWizard title="Upload" currentStep={2}>
      <div className="flex w-full shrink grow flex-col">
        <div
          className="border-background } relative flex h-full w-full flex-col items-center justify-center gap-8 p-8"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div
            className={cn(
              'bg-accent/80 pointer-events-none absolute inset-6 flex items-center justify-center backdrop-blur-xs transition-all select-none',
              {
                'scale-95 opacity-0': !isDragging,
              },
            )}
          >
            <h1 className="text-accent-foreground text-xl font-medium">
              Drop your recordings to upload
            </h1>
            <div className="border-accent-foreground/60 absolute top-0 left-0 size-20 rounded-tl-sm border-t-2 border-l-2" />
            <div className="border-accent-foreground/60 absolute top-0 right-0 size-20 rounded-tr-sm border-t-2 border-r-2" />
            <div className="border-accent-foreground/60 absolute bottom-0 left-0 size-20 rounded-bl-sm border-b-2 border-l-2" />
            <div className="border-accent-foreground/60 absolute right-0 bottom-0 size-20 rounded-br-sm border-r-2 border-b-2" />
            <div className="border-accent-foreground/60 absolute top-0 left-1/2 w-30 -translate-x-1/2 border-t-2" />
            <div className="border-accent-foreground/60 absolute bottom-0 left-1/2 w-30 -translate-x-1/2 border-t-2" />
            <div className="border-accent-foreground/60 absolute top-1/2 left-0 h-30 -translate-y-1/2 border-l-2" />
            <div className="border-accent-foreground/60 absolute top-1/2 right-0 h-30 -translate-y-1/2 border-l-2" />
          </div>

          <header className="max-w-4xl space-y-3 p-8 text-center">
            <Heading1>
              Welcome!
              <br />
              Let's give Happitu something to work with.
            </Heading1>
            <p className="text-muted-foreground max-w-lg"></p>
          </header>
          <div
            className={`bg-secondary flex w-full max-w-2xl cursor-pointer flex-col items-center gap-4 rounded-lg p-12 transition-colors ${
              isDragging
                ? 'border-lime-green bg-lime-50'
                : 'border-border-dark hover:border-foreground/30'
            }`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Icon name="upload-simple" className="text-muted-foreground size-10" />
            <p className="text-foreground font-medium">
              Drag and drop your call recordings here
            </p>
            <p className="text-muted-foreground text-sm">MP3, WAV, M4A, or OGG files</p>
            <label>
              <span className={buttonVariants({ variant: 'lime', size: 'lg' })}>
                <Icon name="folder-simple-dashed" className="mr-2 size-4" />
                Select Recordings
              </span>
              <input
                ref={inputRef}
                type="file"
                accept={AUDIO_ACCEPTED_EXTENSIONS.join(',')}
                multiple
                onChange={handleInputChange}
                className="hidden"
              />
            </label>
          </div>
          <Notice type="tip" className="max-w-2xl">
            <p>
              <strong className="text-foreground/70 font-semibold">
                Upload 10 or more recordings from your team.
              </strong>
              <br />
              The more you bring, the sharper Insights gets at spotting patterns and the
              moments worth your attention.
            </p>
          </Notice>
        </div>
        <footer className="border-background flex w-full items-center justify-center border-t-4 p-4">
          <div className="text-foreground/50 text-center">
            Uploads will begin automatically when you select or drop files
          </div>
        </footer>
      </div>
    </SetupWizard>
  )
}
